import { styled } from '@mui/material/styles';
import { Box, Drawer, ListItem, ListItemButton } from '@mui/material';

export const SBWIDTH = 300;

// --- useStyles (default) ---

export const PadBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const SystemBlock = styled('div')({
  pageBreakBefore: 'always',
  clear: 'both',
});

export const HeaderRow = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
});

export const HeaderChild = styled('span')({
  // PFM-2615: stack the name and its weight/criteria sub-label vertically.
  // Previously HeaderCriteria was `position: absolute` (pinned to the bottom of
  // this box). With the SystemHeader's 48px line-height the name text is
  // vertically centered, so the absolutely-positioned weight label rendered on
  // top of the name (overlap regression from the MUI v5->v9 upgrade). Laying
  // the children out in a flex column keeps the weight cleanly below the name.
  // Top-align the column so the name (first line) sits at the top next to the
  // checkbox and the weight hangs below it — the header itself top-aligns its
  // row when a weight is present (see SystemHeader).
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  margin: 0,
  overflow: 'hidden',
  position: 'relative',
});

export const HeaderChildName = styled('span')({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  overflowX: 'auto',
  // PFM-2615: the SystemHeader sets line-height:48px (to clamp single-line
  // headers to 48px tall). When a weight/criteria sub-label is stacked below the
  // name, that 48px line on the name pushed the column to ~62px and made the
  // weighted header rows taller than the others (staggered). A normal line-height
  // lets the name + weight fit within the header's 48px min-height, so weighted
  // and unweighted headers stay the same height.
  lineHeight: 1.2,
});

export const HeaderChildScore = styled('span')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  '@media print': {
    display: 'block',
  },
  marginLeft: theme.spacing(1),
  // System header uses `as="h2"` (browser-bold); keep the score at normal weight.
  fontWeight: 'normal',
}));

export const HeaderCriteria = styled('span')(({ theme }) => ({
  // PFM-2615: kept in normal flow (below the name in HeaderChild's column
  // layout) instead of `position: absolute`, which overlapped the name after
  // the MUI v9 upgrade. A real line-height prevents the text from collapsing
  // onto the name baseline.
  lineHeight: 1.2,
  fontSize: '12px',
  color: theme.palette.text.secondary,
  fontWeight: 'normal',
}));

// Toggle layout/score states via class names, not styled() props. Dynamic props
// make Emotion rebuild CSS on every render (including resize), which is costly
// on a long scorecard. These classes are generated once and reused.
export const NA_SCORE_CLASS = 'na-score';
export const SCORE_VALUE_CLASS = 'score-value';
export const IS_COURSE_CLASS = 'is-course';
export const PRINT_VIEW_CLASS = 'print-view';

export const SidebarHeaderChild = styled('span')(({ theme }) => ({
  marginRight: 15,
  fontWeight: 300,
  [`&.${NA_SCORE_CLASS}`]: {
    color: theme.palette.text.secondary,
  },
}));

// ListSubheader row height (MUI default). Checklist headers stick just below
// this plus the system header's sticky top padding.
export const SYSTEM_HEADER_ROW_HEIGHT = 48;

export const SystemHeader = styled('div')(({ theme }) => ({
  // In MUI v9, `as={ListSubheader}` no longer reliably carries through
  // ListSubheader's default `position: sticky` style — emotion's styled('div')
  // generates a className whose specificity wins, leaving the header un-sticky.
  // Pin the sticky positioning explicitly so checklist headers don't slide
  // under the system header on scroll.
  position: 'sticky',
  top: 0,
  zIndex: 3,
  paddingLeft: theme.spacing(2),
  // margin-top is left behind when the header sticks at top: 0. Keep the in-flow
  // 1em gap, but move a slice of it into padding so a little space above the
  // text remains (with paper behind it) while sticky.
  paddingTop: theme.spacing(1),
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  marginTop: `1em`,
  // PFM-2599 QA round 2: in v5, `as={ListSubheader}` applied
  // ListSubheader's line-height: 48px, which constrained the header's height
  // so checklist sticky headers stacked underneath cleanly. v9 no longer
  // applies that, and the bare h2 children inherit the browser-default
  // ~0.83em top/bottom margins (plus h2's 1.5em font-size), nearly doubling
  // the header's vertical footprint and pushing the checklist header out of
  // view. Clamp the row's height and reset child margins so the superset
  // header sits flush at 48px tall like prod.
  minHeight: `calc(${SYSTEM_HEADER_ROW_HEIGHT}px + ${theme.spacing(1)})`,
  lineHeight: `${SYSTEM_HEADER_ROW_HEIGHT}px`,
  // Only reset vertical heading margins — keep horizontal spacing (e.g. score gap).
  '& h2, & h3, & h4': {
    marginTop: 0,
    marginBottom: 0,
  },
  // PFM-2615: when the header carries a weight/criteria sub-label, top-align the
  // row so the checkbox/score and the name line up at the top and the weight sits
  // below the name (matching prod). Unweighted headers keep their centered single
  // line. The weight label is tagged with `.weight-criteria` (see SystemScore).
  '&:has(.weight-criteria)': {
    alignItems: 'flex-start',
    '& h2': {
      lineHeight: 1.2,
    },
  },
}));

export const ChecklistMenuHeader = styled('span')(({ theme }) => ({
  display: 'inline',
  fontSize: 'small',
  [`&.${SCORE_VALUE_CLASS}`]: {
    fontWeight: 300,
  },
  [`&.${NA_SCORE_CLASS}`]: {
    color: theme.palette.text.secondary,
  },
}));

export const ContentWrapper = styled('div')({
  display: 'flex',
  width: '100%',
  height: 'calc(100% - 176px)',
  '@media print': {
    position: 'relative',
    display: 'block',
    height: 'auto !important',
  },
  [`&.${IS_COURSE_CLASS}`]: {
    height: 'unset',
  },
  [`&.${PRINT_VIEW_CLASS}`]: {
    flexDirection: 'column',
  },
});

export const ListItemWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  alignItems: 'center',
});

export const ListItemWrapChild = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

export const ExpandIcon = styled('span')({
  position: 'absolute',
  right: 5,
});

export const RightContent = styled('div')(({ theme }) => ({
  position: 'relative',
  width: `calc(100% - ${SBWIDTH}px)`,
  maxWidth: theme.breakpoints.values['lg'],
  [theme.breakpoints.up('xl')]: {
    marginLeft: `calc(50% - ${theme.breakpoints.values['lg'] / 2}px - ${SBWIDTH}px)`,
  },
  right: 0,
  padding: theme.spacing(1),
  [theme.breakpoints.down('lg')]: {
    padding: 0,
    width: '100%',
  },
  '@media print': {
    width: '100%',
  },
  [`&.${IS_COURSE_CLASS}, &.${PRINT_VIEW_CLASS}`]: {
    width: '100%',
    [theme.breakpoints.up('xl')]: {
      marginLeft: 'unset',
    },
  },
  [`&.${IS_COURSE_CLASS}`]: {
    maxWidth: 'unset',
  },
}));

export const ActiveEditContainer = styled('div')({
  // Edit mode unmounts every other section, so the page is often shorter than
  // the viewport. Without this, scrollY clamps to 0 and the section header
  // cannot sit at the top the way it does after closing edit.
  minHeight: '100vh',
});

export const ToggleSidebar = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
  '@media print': {
    display: 'none',
  },
}));

export const ShowImages = styled('div')(({ theme }) => ({
  color: theme.palette.secondary.main,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  '@media print': {
    display: 'none',
  },
}));

// Active/parent rows use MUI ListItemButton `selected` (primary-tint),
// matching the library document sidebar. Inactive rows stay on paper.
// Bold only the section name — scores stay normal weight.
export const SIDEBAR_SECTION_NAME = 'sidebar-section-name';

export const ChecklistActiveButton = styled(ListItemButton)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'center',
  paddingLeft: theme.spacing(3),
  [`&.Mui-selected .${SIDEBAR_SECTION_NAME}`]: {
    fontWeight: 'bold',
  },
}));

export const SystemActiveButton = styled(ListItemButton)({
  [`&.Mui-selected .${SIDEBAR_SECTION_NAME}`]: {
    fontWeight: 'bold',
  },
});

// Wraps accordion/checklist rows below Overall — same elevation/radius as HomeCard.
export const SidebarListCard = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

export const LeftSidebar = styled('div')(({ theme }) => ({
  width: SBWIDTH,
  position: 'sticky',
  alignSelf: 'flex-start',
  top: 0,
  overflowY: 'auto',
  height: '100%',
  maxHeight: '100vh',
  // backgroundColor: theme.palette.background.paper,
}));

export const LeftSidebarMobile = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '80%',
  },
});

export const DrawerPaper = styled('div')({
  width: SBWIDTH,
  top: 177,
  bottom: 0,
  height: 'auto',

  '& .MuiList-padding': {
    padding: 0,
  },
});

export const OverallScoreItem = styled(ListItem)({
  textAlign: 'center',
  fontWeight: 'bold',
  justifyContent: 'center',
});

export const TaskBadge = styled('span')({
  position: 'absolute',
  top: 4,
  right: 4,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: 'red',
  zIndex: 1,
});

// --- filterStyles ---

export const FilterContainer = styled('div')({
  top: 0,
  zIndex: 3,
  pointerEvents: 'auto',
  overflow: 'hidden',

  '@media print': {
    display: 'none',
  },
});

export const FilterSection = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const Droptext = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontStyle: 'italic',
  margin: theme.spacing(1),

  '& .difflink': {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
  },
}));

// --- multiStyles ---

export const ScorecardTitle = styled('div')(({ theme }) => ({
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));
